import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import type { PbjBreaksRemovedRow } from "../../../utils/models";
import { usePbjBreaksRemoved } from "../hooks/usePbjAudit";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { boldNameColumn, hoursColumn, monospaceIdColumn } from "../pbjGridCells";
import { formatAuditDate } from "../pbjGridFormat";

type Props = {
	startDate: string;
	endDate: string;
};

/** Unique grid id — fold in the array index so equal (emp, day) rows never collide. */
type BreaksRemovedGridRow = PbjBreaksRemovedRow & { __rowId: string };

/**
 * Breaks Removed audit — the (employee, day) rows whose worked hours were trimmed by an
 * unpaid-break rule before filing. Renders as a section card on the Audit canvas, sharing the
 * grid + header treatment of its sibling audit sections.
 */
export const BreaksRemovedTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = usePbjBreaksRemoved(startDate, endDate);

	const rows = useMemo<BreaksRemovedGridRow[]>(() => (data ?? []).map((r, i) => ({ ...r, __rowId: `${i}|${r.empId}|${r.date}` })), [data]);

	const columns: GridColDef<BreaksRemovedGridRow>[] = useMemo(
		() => [
			monospaceIdColumn<BreaksRemovedGridRow>("empId", t("pbj_model.audit.breaks_removed.cols.id")),
			boldNameColumn<BreaksRemovedGridRow>("name", t("pbj_model.audit.breaks_removed.cols.name")),
			{
				field: "jobDescription",
				headerName: t("pbj_model.audit.breaks_removed.cols.job"),
				minWidth: 160,
				flex: 1,
				valueFormatter: (value: string | null) => value || "—",
			},
			{
				field: "date",
				headerName: t("pbj_model.audit.breaks_removed.cols.date"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			hoursColumn<BreaksRemovedGridRow>("breakHoursRemoved", t("pbj_model.audit.breaks_removed.cols.break_hours"), { minWidth: 140 }),
		],
		[t],
	);

	return (
		<SettingsSection
			layout="stack"
			title={t("pbj_model.audit.breaks_removed.section.title")}
			description={t("pbj_model.audit.breaks_removed.section.subtitle")}
			dataTestId="pbj-breaks-removed-section"
			sx={(theme) => sectionCardSx(theme)}
		>
			<SystemDataGrid<BreaksRemovedGridRow>
				rows={rows}
				columns={columns}
				getRowId={(r) => r.__rowId}
				searchableColumns={["empId", "name"]}
				searchPlaceholder={t("pbj_model.audit.breaks_removed.search")}
				loading={isLoading}
				error={isError}
				errorMessage={t("pbj_model.audit.breaks_removed.failed")}
				emptyState={<EmptyState title={t("pbj_model.audit.breaks_removed.empty")} dataTestId="pbj-model-audit-breaks-removed-empty" />}
				defaultPageSize={25}
				containedHeight
				sx={(theme) => dataTableSx(theme)}
				dataTestId="pbj-model-audit-breaks-removed"
			/>
		</SettingsSection>
	);
};
